import * as log from '../util/log';

/**
 * Function type for writing a value to a destination.
 */
export type SyncWFn<T> = (value: T) => Promise<void>;

/**
 * Function type for reading a value from a source.
 */
export type SyncRFn<T> = () => Promise<T>;

/**
 * Function type for getting the current timestamp.
 */
export type NowFn = () => number;

/**
 * Default timestamp function.
 */
const defaultNow = () => Date.now();

/**
 * General class to synchronize a value between a source and a destination.
 */
export class Syncer<T> {
  /**
   * Timestamp of the last sync (successful or not).
   */
  #lastSync: number = 0;

  /**
   * Scheduled timeout for next sync.
   */
  #nextSyncTimer: NodeJS.Timeout | null = null;

  constructor(
    private getValue: SyncRFn<T>,
    private setValue: SyncWFn<T>,
    private interval: number = 30 * 1000, // Default to 30 seconds
    private now: NowFn = defaultNow,
  ) {
    this.#scheduleSync();
  }

  /**
   * Force an immediate sync.
   */
  public async force(v: T) {
    log.debug(`Forcing immediate sync with custom value`);
    try {
      await this.#transferValue(v);
    } catch (err) {
      log.error('Force sync failed:', err as Error);
    } finally {
      this.#lastSync = this.now();
      this.#scheduleSync();
    }
  }

  /**
   * Compute time until next sync.
   */
  #timeUntilNextSync() {
    const elapsed = this.now() - this.#lastSync;
    return this.interval - elapsed;
  }

  /**
   * Schedule the next sync based on the interval and last sync time.
   */
  #scheduleSync() {
    const timeout = Math.max(0, this.#timeUntilNextSync());
    if (this.#nextSyncTimer) {
      clearTimeout(this.#nextSyncTimer);
    }
    this.#nextSyncTimer = setTimeout(() => {
      this.#sync()
        .catch((err) => {
          log.error('Sync failed:', err);
        })
        .finally(() => {
          // Mark the sync time even if it failed.
          this.#lastSync = this.now();
          this.#nextSyncTimer = null;
          this.#scheduleSync();
        });
    }, timeout);
  }

  /**
   * Get value and transfer it.
   */
  async #sync() {
    const v = await this.getValue();
    await this.#transferValue(v);
  }

  /**
   * Call the set value function and mark the sync time.
   */
  async #transferValue(v: T) {
    await this.setValue(v);
    log.debug(`Sync completed at ${this.now()}`);
  }
}
